import { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

import routes from './../../routes';
import { checkIsLogin, getTokenPayload, getUserToken, submitLogout } from '../../hooks/useLogin';

const Header = () => {
	const { pathname } = useLocation();
	const mainMenuRef = useRef();
	const headerRef = useRef();
	const navBarRef = useRef();
	const [homeSelect, setHomeSelect] = useState(true);
	const [navToggler, setNavToggler] = useState(false);
	const [navSelected, setNavSelected] = useState(null);

	const closeGnb = () => {
		setHomeSelect(false);
		setNavToggler(false);
	};

	const handleNavbar = (i, v) => {
		setHomeSelect(true);
		sessionStorage.removeItem("language");
		setNavSelected(i);
		if (v) setNavToggler(false);
	};

	useEffect(() => {
		document.activeElement.blur();

		document.body.classList.toggle('open-gnb', navToggler);

		const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
		const focusableContent = headerRef.current.querySelectorAll(focusableElements);
		const len = focusableContent.length;
		const firstFocusable = focusableContent[0];
		const lastFocusable = focusableContent[len - 1];
		const handleGnbAccessibility = (event) => {
			const isEscapeKey = event.key === 'Esc' || event.key === 'Escape' || event.keyCode === 27;
			const isTabKey = event.key === 'Tab' || event.keyCode === 9;
			if (!(isEscapeKey || isTabKey)) return;
			if (isEscapeKey) return setNavToggler(false);

			if (event.shiftKey) {
				if (document.activeElement === firstFocusable) {
					lastFocusable.focus();
					event.preventDefault();
				}
			} else {
				if (document.activeElement === lastFocusable) {
					firstFocusable.focus();
					event.preventDefault();
				}
			}
		};

		const handlerMouseDown = (e) => {
			if (headerRef.current && !headerRef.current.contains(e.target)) setNavToggler(false);
		};

		if (navToggler) {
			document.addEventListener('keydown', handleGnbAccessibility);
			document.addEventListener('mousedown', handlerMouseDown);
			window.addEventListener('resize', function () {
				setNavToggler(false);
			});
		}

		document.querySelectorAll('.sub-menu').forEach((ele) => {
			if (ele.classList.contains('active')) ele.closest('li.nav-item').classList.add('nav-toggle');
		});

		return () => {
			document.removeEventListener('keydown', handleGnbAccessibility);
			document.removeEventListener('mousedown', handlerMouseDown);
			window.removeEventListener('resize', function () {
				setNavToggler(false);
			});

			document.querySelectorAll('.sub-menu').forEach((ele) => {
				ele.closest('li.nav-item').classList.remove('nav-toggle');
			});
		};
	}, [pathname, navToggler]);

	const [currentMenu, setCurrentMenu] = useState(null);
	const pathParts = pathname.split('/');
	const modifiedPathname = pathParts.slice(0, 3).join('/');
	const currentIndex = routes.findIndex((item) => item.path.includes(modifiedPathname));
	const groupIndex = routes[currentIndex]?.gnbIndex;

	// 로그인
	const [isLogin, setIsLogin] = useState(false);
	
	function logout() {
		submitLogout();

		closeGnb();
	}

	useEffect(() => {
		setCurrentMenu(groupIndex);
		setNavSelected(groupIndex);
		const isNotNavMenu = pathname.includes("/account") || pathname.includes("/mypage") || pathname === "/";
		if(isNotNavMenu) {
			setHomeSelect(true);
		}

		if(getUserToken() !== "" && getTokenPayload().id !== 0) {
			checkIsLogin(setIsLogin);
		} 
	}, [groupIndex]);


	return (
		<header id="header" ref={headerRef}>
			<div>
				<button
					onClick={() => setNavToggler(!navToggler)}
					type="button"
					className="gnb-toggler"
					aria-controls="gnb"
					aria-expanded={navToggler ? 'true' : 'false'}
					aria-label={navToggler ? '전체 메뉴 닫기' : '전체 메뉴 열기'}
				>
					<span>
						<i className="sr-only">전체 메뉴</i>
					</span>
				</button>

				<h1>
					<Link to="/" onClick={closeGnb} className="home" title="홈으로 이동">
						<img src={`${process.env.PUBLIC_URL}/static/images/brand_logo.png`} alt="브랜드 로고" />
					</Link>
				</h1>

				<nav id="gnb" ref={navBarRef} focustab={navToggler ? 0 : -1}>
					<h2 className="sr-only">메인 메뉴</h2>
					<ul className="gnb-nav" ref={mainMenuRef}>
						{navbars.map((menu, index) => (
							<li
								key={index}
								className={`${
									navSelected === index && homeSelect ? 'nav-item nav-toggle' : 'nav-item'
								}`}
							>
								<div className="gnb-mainmenu">
									<Link
										to={menu.url}
										onClick={() => handleNavbar(index, true)}
										className={`gnb-menu-toggle ${
											currentMenu !== undefined
												? currentMenu === index + 1
													? 'active'
													: ''
												: ''
										}`}
									>
										<span>{menu.title}</span>
									</Link>
								</div>
							</li>
						))}
					</ul>
				</nav>

				<div id="header-link-items">
					<ul>
						{isLogin ? (
							<>
								<li>
									<Link to={'/mypage/info'} className="btn" onClick={closeGnb}>
										마이페이지
									</Link>
								</li>
								<li>
									<button
										type="button"
										className="btn"
										onClick={logout}
									>
										로그아웃
									</button>
								</li>
							</>
						) : (
							<>
								<li>
									<Link to={'/account/login'} className="btn" onClick={closeGnb}>
										로그인
									</Link>
								</li>
								<li>
									<Link to={'/account/signup'} className="btn" onClick={closeGnb}>
										회원가입
									</Link>
								</li>
							</>
						)}
					</ul>
				</div>
			</div>
		</header>
	);
};

export default Header;

const navbars = [
	{
		id: 1,
		title: '소개',
		url: '/introduction/summary',
	},
	{
		id: 2,
		title: '프로그램',
		url: '/program/list',
	},
	{
		id: 3,
		title: '기술소개 투자유치 플랫폼',
		url: '/platform/startup',
	},
	{
		id: 4,
		title: '창업기업 소개',
		url: '/hospital/startup',
	},
	{
		id: 5,
		title: '커뮤니티',
		url: '/community/notice',
	},
];
